<script setup lang="ts">
    import { useWindowManager } from '@/composables/useWindowManager';
    import { WINDOW_TASKBAR_HEIGHT, WINDOW_TASKBAR_Z_INDEX } from '@/constants';

    const { t } = useI18n();
    const { windows, activeWindowId, closeWindow, closeAllWindows, minimizeAllWindows, toggleMinimize } = useWindowManager();

    const hasVisibleWindows = computed(() => windows.value.some((w) => !w.isMinimized));

    const titleOf = (headerTitle?: string): string => headerTitle?.trim() || t('window.untitled');
</script>

<template>
    <div v-if="windows.length > 0" class="window-taskbar d-flex align-center" :style="{ height: `${WINDOW_TASKBAR_HEIGHT}px`, zIndex: WINDOW_TASKBAR_Z_INDEX }">
        <div class="window-taskbar__items d-flex align-center">
            <div
                v-for="window in windows"
                :key="window.id"
                class="window-taskbar__item d-flex align-center"
                :class="{
                    'window-taskbar__item--active': activeWindowId === window.id,
                    'window-taskbar__item--minimized': window.isMinimized,
                }"
                :data-tooltip="titleOf(window.headerTitle)"
                @click="toggleMinimize(window.id)"
                @auxclick.middle.prevent="closeWindow(window.id)"
            >
                <v-icon v-if="window.headerIcon" :icon="window.headerIcon" size="16" class="mr-1 flex-shrink-0" />
                <span class="text-truncate">{{ titleOf(window.headerTitle) }}</span>
                <v-btn
                    :icon="mdiClose"
                    variant="text"
                    density="compact"
                    size="x-small"
                    class="window-taskbar__close ml-1 flex-shrink-0"
                    :data-tooltip="t('action.close')"
                    @click.stop="closeWindow(window.id)"
                />
            </div>
        </div>

        <v-divider class="my-1 mx-2" vertical />

        <v-btn
            :icon="mdiChevronDoubleDown"
            variant="text"
            density="compact"
            size="small"
            color="tbGreen"
            class="flex-shrink-0"
            :disabled="!hasVisibleWindows"
            :data-tooltip="t('window.minimizeAll')"
            @click="minimizeAllWindows"
        />
        <v-btn
            :icon="mdiCloseBoxMultipleOutline"
            variant="text"
            density="compact"
            size="small"
            color="error"
            class="flex-shrink-0 ml-1"
            :data-tooltip="t('window.closeAll')"
            @click="closeAllWindows"
        />
    </div>
</template>

<style lang="scss" scoped>
    .window-taskbar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding: 0 6px;
        background: rgb(var(--v-theme-surface));
        border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
    }

    .window-taskbar__items {
        gap: 4px;
        overflow-x: auto;
        overflow-y: hidden;
        min-width: 0;
        flex: 1 1 auto;
        height: 100%;
        scrollbar-width: thin;
    }

    .window-taskbar__item {
        flex: 0 1 auto;
        min-width: 90px;
        max-width: 220px;
        height: 26px;
        padding: 0 4px 0 8px;
        border-radius: 3px;
        border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
        border-bottom-width: 2px;
        border-bottom-color: transparent;
        background: rgba(var(--v-theme-on-surface), 0.04);
        font-size: $font-size-12;
        cursor: pointer;
        user-select: none;

        &:hover {
            background: rgba(var(--v-theme-on-surface), 0.1);
        }
    }

    .window-taskbar__item--active {
        border-bottom-color: rgb(var(--v-theme-tbGreen));
        background: rgba(var(--v-theme-tbGreen), 0.12);
        font-weight: 600;
    }

    .window-taskbar__item--minimized {
        opacity: 0.6;
    }

    .window-taskbar__close {
        opacity: 0.55;

        &:hover {
            opacity: 1;
        }
    }
</style>
